import React, {Component} from 'react';
import {connect} from 'react-redux';
import {Table, Card, Input, Button, Grid, Icon} from 'semantic-ui-react';
import {hasAccessPermission} from '../../../common/permissions/validations';
import {resources, scopes} from '../../../common/permissions/dictionary';
import {checkFactorPattern, isFactorLessThanOrEqualToZero} from '../utils';
import {headerCellTitlesOfData} from '../static/commonStructures';
import {paths} from '../../../common/dictionaries/urls';
import {Actions} from '../../../common/actions';

const fieldsToDisplay = [...headerCellTitlesOfData];

class PriceFactorsEditOrClone extends Component {
  constructor(props) {
    super(props);
    this.state = {'savedPriceFactor': false, 'priceFactor': {}, 'disableSaveButton': false, 'action': ''};
    this.handleCancelClick = this.handleCancelClick.bind(this);
    this.handleSaveClick = this.handleSaveClick.bind(this);
  }

  componentDidMount() {
    if (
      !hasAccessPermission(
        this.props.permissions,
        resources.PriceFactorsMaintenance,
        scopes.Edit
      )
    ) {
      this.props.push(paths.AccessDenied);
    }

    const {recordId, action} = this.props.location.state;
    this.props.clearPriceFactorsList();

    const urlParams = this.getPriceFactorsQueryParams(recordId);
    this.props.getPriceFactorsList(urlParams);

    if (action !== this.state.action) {
      this.setState({'action': action});
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (this.props.priceFactorsList !== prevProps.priceFactorsList) {
      this.setState({
        'priceFactor': Object.assign({}, this.props.priceFactorsList[0]),
      });
    }

    if (prevState.savedPriceFactor !== this.state.savedPriceFactor && this.state.savedPriceFactor) {
      this.props.push(paths.PriceFactorsMaintenance);
    }
  }

  getPriceFactorsQueryParams(recordId) {
    const params = {
      'productCode': recordId,
      'priceFactorGroup': '',
      'limit': 1,
      'offset': 0,
    };
    return params;
  }

  checkFactorValues(priceFactor) {
    for (let i = 0; i < headerCellTitlesOfData.length; i++) {
      if (headerCellTitlesOfData[i].name.includes('Factor')) {
        if (
          checkFactorPattern(
            priceFactor[headerCellTitlesOfData[i].serializedName],
            headerCellTitlesOfData[i].name
          ) ||
          isFactorLessThanOrEqualToZero(
            priceFactor[headerCellTitlesOfData[i].serializedName],
            headerCellTitlesOfData[i].name
          )
        ) {
          return true;
        }
      }
    }
    return false;
  }

  isRowInvalid(priceFactor) {
    return (
      !priceFactor.productCode ||
      !priceFactor.tdeGroup ||
      !priceFactor.groupDescription ||
      !priceFactor.factorRetail ||
      !priceFactor.factorWholesale ||
      this.checkFactorValues(priceFactor)
    );
  }

  handleCancelClick() {
    this.props.goBack();
  }

  async handleSaveClick() {
    const {priceFactor} = this.state;
    await this.props.upsertPriceFactor([priceFactor]);
    this.setState({'savedPriceFactor': true});
  }

  isInputDisabled(column) {
    const {action} = this.state;
    return action === 'edit' && column.name === 'Product code';
  }

  handleChangeValue(event, serializedName) {
    const {priceFactor} = this.state;
    priceFactor[serializedName] = event.target.value.toUpperCase();
    this.setState({'priceFactor': priceFactor});
    this.setState({'disableSaveButton': this.isRowInvalid(priceFactor)});
  }

  renderTitleAndActions() {
    const {disableSaveButton, action} = this.state;
    return (
      <Card raised fluid>
        <Grid.Row>
          <div className='title-bar'>
            <div className='title'>
              <Icon name='arrow left' onClick={this.handleCancelClick} />
              <h2>
                {action === 'clone' ? 'Clonar' : 'Editar'} factores de precio
              </h2>
              <div className='title-bar-actions'>
                <Button onClick={this.handleCancelClick} secondary>
                  Cancelar
                </Button>
                <Button
                  primary
                  onClick={this.handleSaveClick}
                  className='save-button'
                  disabled={disableSaveButton}
                >
                  Guardar
                </Button>
              </div>
            </div>
          </div>
        </Grid.Row>
      </Card>
    );
  }

  renderTable() {
    return (
      <Card fluid raised>
        <div className='horizontal-scroll'>
          <Table>
            {this.renderTableHeader()}
            {this.renderTableBody()}
          </Table>
        </div>
      </Card>
    );
  }

  renderTableHeader() {
    return (
      <Table.Header>
        <Table.Row>{this.renderTableHeaderCells()}</Table.Row>
      </Table.Header>
    );
  }

  renderTableHeaderCells() {
    return fieldsToDisplay.map((element) => {
      return (
        <Table.HeaderCell key={`header${element.name}`}>
          {element.name}
        </Table.HeaderCell>
      );
    });
  }

  renderTableBody() {
    return <Table.Body>{this.renderPriceFactors()}</Table.Body>;
  }

  renderPriceFactors() {
    const {priceFactor} = this.state;

    if (!priceFactor) {
      return null;
    }

    return (
      <Table.Row>
        {headerCellTitlesOfData.map((column) => {
          return this.renderDataInCells(priceFactor, column);
        })}
      </Table.Row>
    );
  }

  renderInvalidFactors(invalidFactors) {
    if (invalidFactors) {
      return (
        <div className='invalid-value'>
          El factor debe tener hasta 10 decimales
        </div>
      );
    }
  }

  renderNegativeFactors(negativeFactors) {
    if (negativeFactors) {
      return (
        <div className='invalid-value'>El factor debe ser mayor que cero</div>
      );
    }
  }

  renderDataInCells(priceFactor, column) {
    const invalidFactors = checkFactorPattern(
      priceFactor[column.serializedName],
      column.name
    );
    const negativeFactors = isFactorLessThanOrEqualToZero(
      priceFactor[column.serializedName],
      column.name
    );
    return (
      <Table.Cell
        textAlign='center'
        key={`cell${priceFactor}${column.name}`}
        style={{'width': column.width}}
      >
        <Input
          disabled={this.isInputDisabled(column)}
          value={priceFactor[column.serializedName] || ''}
          maxLength={column.maxLength}
          style={{'width': column.width}}
          required={column.required}
          error={invalidFactors || negativeFactors}
          onChange={(event) =>
            this.handleChangeValue(event, column.serializedName)
          }
          inline='centered'
          key={`input${priceFactor}${column}`}
        />
        {this.renderInvalidFactors(invalidFactors)}
        {this.renderNegativeFactors(negativeFactors)}
      </Table.Cell>
    );
  }

  render() {
    return (
      <div className='price-factors'>
        {this.renderTitleAndActions()}
        {this.renderTable()}
      </div>
    );
  }
}

const mapStateToProps = (state, ownProps) => {
  return {
    'push': ownProps.history.push,
    'goBack': ownProps.history.goBack,
    'priceFactorsList': state.priceFactorsList.list,
    'permissions': state.session.permissions,
    'location': ownProps.history.location,
  };
};

const {
  getPriceFactorsList,
  clearPriceFactorsList,
  upsertPriceFactor,
  withRouter,
} = Actions;
export default withRouter(connect(mapStateToProps, {
  getPriceFactorsList,
  clearPriceFactorsList,
  upsertPriceFactor,
})(PriceFactorsEditOrClone));
